import { OverlayMode } from '../../overlays/overlay';
import {
  ActivityMaps,
  ActivityMapsHost,
  ClickFacet,
  ClicksBySelector,
  ElemStats,
  MatchingElement,
} from '../../page-insights/activitymaps';
import { MobilePageRenderer } from '../renderer';
import { CanvasRenderer, ViewMatch } from '@fullstory/playback-mobile';
import { getPercentString } from '@fullstory/util/fmt';
import {
  ClickAreaOverlayDisplayOptions,
  ClickMapDisplayOptions,
  ConversionOverlayDisplayOptions,
} from '@fullstory/page-insights-ui';
import { maxHighlightedElements } from '../../page-insights/activitymapsconst';
import { intl } from '@fullstory/intl';
import { parseSelector } from '@fullstory/selectors';
import { defaultClickBySelectorSort } from '../../page-insights/document_lookup';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import { elemIdent, elemStatsIdent } from '../../page-insights/ident';
import { Renderer } from '@fullstory/renderer-shared';
import {
  computeFocusPaths,
  computePropertyValuesConfig,
  PropertyValuesConfig,
  satisfiesConstraints,
} from '../../page-insights/properties';

const thresholdTallElement = 0.7;
const thresholdLargeElement = 0.5;

export class MobileActivityMaps implements ActivityMaps, OverlayMode {
  private highlighted: { [key: string]: MobileHighlight } = {};

  constructor(private _renderer: MobilePageRenderer, private _host: ActivityMapsHost) {}

  start() {}

  stop() {
    this.clear();
  }

  clear() {
    this.highlighted = {};
    this._renderer.overlays().clearAreaOverlays();
    this._renderer.overlays().clearHighlights();
  }

  enableMapInsights(enable: boolean) {
    this._renderer.view().switchClass('map-insights', enable);
  }

  highlightClickArea(clicks: ClickFacet, displayOptions: ClickAreaOverlayDisplayOptions) {
    this.clear();
    // TODO Mobile support to be added later
  }

  highlightConversionZones(
    clickFacet: ClickFacet,
    displayOptions: ConversionOverlayDisplayOptions,
  ) {
    this.clear();
    this.setExportMode(displayOptions.exportMode);

    const canvas = this._renderer.canvasRenderer();

    const focusPaths = computeFocusPaths(displayOptions.focusSelectors ?? [], this._renderer);
    const propertyValuesConfig = computePropertyValuesConfig(
      focusPaths,
      displayOptions.groupByProperty,
      this._renderer,
    );

    const matches = queryCanvasForSelectors(
      canvas,
      clickFacet,
      focusPaths,
      propertyValuesConfig,
      this._renderer,
    )
      .sort((a, b) => defaultClickBySelectorSort(a.clicks, b.clicks))
      .splice(0, maxHighlightedElements);

    const elemStats: ElemStats[] = [];
    matches.forEach(match => {
      this.highlighted[elemStatsIdent(match.stats())] = match;
      elemStats.push(match.getStats(clickFacet));
    });

    this._renderer.overlays().placeConversionHighlights({
      host: this._host,
      elems: matches,
      displayOptions,
      playbackContainer: this._renderer.view().scaled,
    });

    // Send it back out.
    this._host.renderStats(clickFacet, elemStats, this._renderer.getNativePlatform(), []);
  }

  highlight(clicks: ClickFacet, displayOptions: ClickMapDisplayOptions) {
    this.clear();
    this.setMapMode(displayOptions.showEngagementMaps);
    this.setExportMode(displayOptions.exportMode);

    const canvas = this._renderer.canvasRenderer();

    const focusPaths = computeFocusPaths(displayOptions.focusSelectors ?? [], this._renderer);
    const propertyValuesConfig = computePropertyValuesConfig(
      focusPaths,
      displayOptions.groupByProperty,
      this._renderer,
    );

    let matches = queryCanvasForSelectors(
      canvas,
      clicks,
      focusPaths,
      propertyValuesConfig,
      this._renderer,
    );
    matches = matches.splice(0, Math.min(maxHighlightedElements, matches.length));
    matches.sort((a, b) => defaultClickBySelectorSort(a.clicks, b.clicks));

    matches.forEach(match => {
      this.highlighted[elemStatsIdent(match.stats())] = match;
    });

    this._renderer
      .overlays()
      .highlight(this._host, matches, displayOptions, displayOptions.exportMode);
    this._host.renderStats(
      clicks,
      matches.map(m => m.getStats(clicks)),
      this._renderer.getNativePlatform(),
      [],
    );
  }

  setExportMode(enable: boolean) {
    if (enable) {
      this._renderer.view().addClass('export-mode');
    } else {
      this._renderer.view().removeClass('export-mode');
    }
  }

  select(selector: string, propertyValue: string | undefined, selected: boolean = true) {
    if (!selected) {
      // Deprecated option. Do nothing. This option will be removed.
      return;
    }
    const highlight = this.highlighted[elemIdent(selector, propertyValue)];
    if (highlight) {
      this._renderer.overlays().select(highlight);
    }
  }

  setMapMode(enable: boolean) {
    if (enable) {
      this._renderer.view().addClass('is-map-mode');
    } else {
      this._renderer.view().removeClass('is-map-mode');
    }
  }
}

function filterBlockedOrLargeViewMatches(
  match: ViewMatch,
  canvasArea: number,
  canvas: CanvasRenderer,
) {
  if (match.view.isBlocked) return false;

  const position = match.view.position;
  const width = position.width();
  const height = position.height();

  const areaRatio = (width * height) / canvasArea;
  const verticalRatio = height / canvas.height();
  const tooLarge = areaRatio > thresholdLargeElement || verticalRatio > thresholdTallElement;

  return !tooLarge;
}

function satisfiesConstraintsForViewMatch(
  rawPropertyValue: string | undefined,
  match: ViewMatch,
  focusPaths: Set<string> | undefined,
  propertyValuesConfig: PropertyValuesConfig | undefined,
  renderer: Renderer,
): boolean {
  return satisfiesConstraints(
    rawPropertyValue,
    match.path.toString(),
    focusPaths,
    propertyValuesConfig,
    renderer,
  );
}

// queryCanvasForSelectors queries the CanvasRenderer for matching views using the selectors found in the ClickFacet.
//
// TODO(dutton): build heuristics to merge similiar selectors, like in the web-equivalent createElemAndStats
function queryCanvasForSelectors(
  canvas: CanvasRenderer,
  facet: ClickFacet,
  focusPaths: Set<string> | undefined,
  propertyValuesConfig: PropertyValuesConfig | undefined,
  renderer: Renderer,
): MobileHighlight[] {
  const canvasArea = canvas.width() * canvas.height();

  const highlights = facet.clicks
    .map(click => {
      let selectors: SelectorChain[] = [];
      const selector = click.selector.raw;

      // If the selector is empty, then the named element is archived and we don't display it
      if (click.namedElementId && selector !== '') {
        // Commas are allowed in attributes (e.g., uistackview[label="some text, with a comma"]). We can't blindly split on a comma.
        let inAttr = false;
        let isEscaping = false;
        let previousIndex = 0;

        for (let idx = 0; idx < selector.length; idx++) {
          const charAtIdx = selector[idx];
          if (charAtIdx === '\\') {
            isEscaping = true;
          } else if (isEscaping) {
            isEscaping = false;
          } else if (!isEscaping) {
            if (charAtIdx === '[') {
              inAttr = true;
            } else if (charAtIdx === ']') {
              inAttr = false;
            } else if (!inAttr && charAtIdx === ',') {
              selectors.push(parseSelector(selector.slice(previousIndex, idx)));
              previousIndex = idx + 1; // skip the comma
            }
          }
        }
        selectors.push(parseSelector(selector.slice(previousIndex)));
      } else {
        selectors = [click.selector.parsed];
      }

      const matches = selectors.flatMap(value =>
        canvas.querySelectorAll(value).filter(
          match =>
            filterBlockedOrLargeViewMatches(match, canvasArea, canvas) &&
            satisfiesConstraintsForViewMatch(
              click.propertyValue,
              match,
              focusPaths,
              propertyValuesConfig,
              renderer,
            ) &&
            !!canvas.boundingBox(match.view), // Filter out any matches that don't have a bounding box
        ),
      );

      if (matches.length === 0) {
        return null;
      }

      return new MobileHighlight(canvas, matches, click, facet);
    })
    .filter((x): x is MobileHighlight => !!x);

  return highlights;
}

export class MobileHighlight implements MatchingElement {
  private _statistics: ElemStats;

  constructor(
    private canvas: CanvasRenderer,
    private viewMatches: ViewMatch[],
    public clicks: ClicksBySelector,
    facet: ClickFacet,
  ) {
    this._statistics = this.getStats(facet);
  }

  stats(): ElemStats {
    return this._statistics;
  }

  selector(): string {
    return this.clicks.selector.raw;
  }

  r(): DOMRect {
    if (this.viewMatches.length === 0) {
      return null;
    }

    for (const match of this.viewMatches) {
      const box = this.canvas.boundingBox(match.view);
      if (!box) {
        continue;
      }

      return new DOMRect(box.x1, box.y1, box.width(), box.height());
    }

    return null;
  }

  allRects(): DOMRect[] {
    const rects = this.viewMatches
      .map(match => {
        const box = this.canvas.boundingBox(match.view);
        if (!box) {
          return null;
        }

        return new DOMRect(box.x1, box.y1, box.width(), box.height());
      })
      .filter(r => r !== null);

    return rects;
  }

  isNamedElement(): boolean {
    return !!this._statistics.NamedElementId;
  }

  getStats(facet: ClickFacet): ElemStats {
    const clicks = this.clicks;

    // TODO(dutton): share with web version
    const primary = {
      Name: intl.formatMessage(
        {
          defaultMessage:
            'Total {clickType, select, 2 {dead clicked} 3 {error clicked} 4 {rage clicked} other {clicked}}',
          id: 'NBS+RR',
        },
        {
          clickType: facet.type,
        },
      ),
      Val: intl.formatMessage(
        {
          defaultMessage:
            '{clickCount, number} {clickType, select, 2 {dead clicks} 3 {error clicks} 4 {rage clicks} other {clicks}} \u2219 {percentage}',
          id: 'f9VAnF',
        },
        {
          clickCount: clicks.count,
          clickType: facet.type,
          percentage: getPercentString(clicks.count, facet.totalClicks),
        },
      ),
      InfoText: intl.formatMessage(
        {
          defaultMessage:
            '{clickCount, number} ({percentage}) of {clickTotal, number} {clickType, select, 2 {dead clicks} 3 {error clicks} 4 {rage clicks} other {clicks}} on this Page',
          id: '/GLSBL',
        },
        {
          clickCount: clicks.count,
          clickTotal: facet.totalClicks,
          percentage: getPercentString(clicks.count, facet.totalClicks),
          clickType: facet.type,
        },
      ),
    };

    const secondary = {
      Name: intl.formatMessage(
        {
          defaultMessage:
            'Users {clickType, select, 2 {dead clicked} 3 {error clicked} 4 {rage clicked} other {clicked}}',
          id: 'LXknWv',
        },
        {
          clickType: facet.type,
        },
      ),
      Val: intl.formatMessage(
        {
          defaultMessage:
            '{userCount, number} {userCount, plural, one {user} other {users}} \u2219 {percentage}',
          id: 'bQfmui',
        },
        {
          userCount: clicks.users,
          percentage: getPercentString(clicks.users, facet.matchingUsers),
        },
      ),
      InfoText: intl.formatMessage(
        {
          defaultMessage:
            '{userCount, number} ({percentage}) of {userTotal, number} users who clicked on this Page',
          id: 'o+vwvI',
        },
        {
          userCount: clicks.users,
          userTotal: facet.matchingUsers,
          percentage: getPercentString(clicks.users, facet.matchingUsers),
        },
      ),
    };

    let footnote = '';
    if (this.viewMatches.length > 1) {
      footnote = `(${this.viewMatches.length} possible elements)`;
    }

    return {
      NamedElementId: clicks.namedElementId,
      Parsed: clicks.selector.parsed,
      Selector: clicks.selector.raw,
      HumanReadableSelector: clicks.selector.humanReadable,
      PropertyValue: clicks.propertyValue,
      Type: facet.type,
      NumClicks: clicks.count,
      NumUsers: clicks.users,
      NumElements: this.viewMatches.length,
      Stats: [primary, secondary],
      Footnote: footnote,
      // TODO(dutton): does VisibilityInfo make sense for Mobile sessions?
      VisibilityInfo: {
        ExistsInDom: true,
        IsInViewport: true,
        CouldBeClicked: true,
        IsOnTop: true,
        ExistsInTheShadow: false,
      },
    };
  }
}
